import { useCallback, useMemo } from 'react';
import { useFiltersContext } from '../HBFilters.context';
import {
    TFilterField,
    IFilter,
    TFilterFieldType,
    IFilterOption
} from '../HBFilters.types';
import { flushSync } from 'react-dom';

export function useFilters() {
    const {
        availableFields,
        activeFilters,
        filtersById,
        setFilters,
        onChange,
        onAddFilter,
        onDeleteFilter,
        onFilterActivated,
        onRemoveAll,
        texts
    } = useFiltersContext();

    // Memoized derived state
    const usedIds = useMemo(
        () => new Set(activeFilters.map(f => f.id)),
        [activeFilters]
    );
    const availableFieldsToAdd = useMemo(
        () => availableFields.filter(f => !usedIds.has(f.id)),
        [availableFields, usedIds]
    );

    // Filter update handlers
    const updateFilters = useCallback(
        (newFilters: IFilter[]) => {
            flushSync(() => {
                setFilters(newFilters);
            });
            onChange(newFilters.map(({ id, value }) => ({ id, value })));
        },
        [setFilters, onChange]
    );

    const addFilter = useCallback(
        (field: TFilterField) => {
            if (!field) {
                return;
            }

            const initialValue =
                field.type === TFilterFieldType.MultiSelect ? [] : null;
            const newFilter: IFilter = {
                id: field.id,
                field,
                value: initialValue,
                isActive: false,
                isNew: true
            };
            updateFilters([
                ...activeFilters.map(f => ({ ...f, isNew: false })),
                newFilter
            ]);
            onAddFilter?.(newFilter);
        },
        [activeFilters, updateFilters, onAddFilter]
    );

    const deleteFilter = useCallback(
        (id: string) => {
            updateFilters(activeFilters.filter(f => f.id !== id));
            onDeleteFilter?.(filtersById[id]);
        },
        [activeFilters, updateFilters, onDeleteFilter, filtersById]
    );

    const updateFilter = useCallback(
        (id: string, value: IFilterOption | null | IFilterOption[]) => {
            const wasPreviouslyActive = filtersById[id]?.isActive;
            const isActiveNow = Array.isArray(value)
                ? value.length > 0
                : !!value;

            const newFilters = activeFilters.map(f =>
                f.id === id
                    ? {
                          ...f,
                          value,
                          isActive: isActiveNow
                      }
                    : f
            );
            updateFilters(newFilters);
            if (!wasPreviouslyActive && isActiveNow) {
                onFilterActivated?.(newFilters.find(f => f.id === id));
            }
        },
        [activeFilters, updateFilters, onFilterActivated, filtersById]
    );

    const removeAll = useCallback(() => {
        updateFilters([]);
        onRemoveAll?.();
    }, [updateFilters, onRemoveAll]);

    // Option selection handlers
    const toggleSingleSelect = useCallback(
        (id: string, option: IFilterOption) => {
            const currentFilter = filtersById[id];
            if (!currentFilter) {
                return;
            }

            const currentValue = currentFilter.value as IFilterOption | null;
            const newValue =
                currentValue?.value === option.value ? null : option;
            updateFilter(id, newValue);
        },
        [filtersById, updateFilter]
    );

    const toggleMultiSelect = useCallback(
        (id: string, option: IFilterOption) => {
            const currentFilter = filtersById[id];
            if (!currentFilter) {
                return;
            }

            const currentValue = currentFilter.value as IFilterOption[];
            const selectedSet = new Set(currentValue.map(v => v.value));
            const newValue = selectedSet.has(option.value)
                ? currentValue.filter(v => v.value !== option.value)
                : [...currentValue, option];
            updateFilter(id, newValue);
        },
        [filtersById, updateFilter]
    );

    const clearSelection = useCallback(
        (id: string) => {
            const filter = filtersById[id];
            if (!filter) {
                return;
            }

            const newValue =
                filter.field.type === TFilterFieldType.MultiSelect ? [] : null;
            updateFilter(id, newValue);
        },
        [filtersById, updateFilter]
    );

    return {
        // Context state
        availableFields,
        activeFilters,
        filtersById,
        texts,

        // Derived state
        availableFieldsToAdd,

        // Filter management
        addFilter,
        deleteFilter,
        updateFilter,
        removeAll,

        // Option selection
        toggleSingleSelect,
        toggleMultiSelect,
        clearSelection
    };
}
